import React from 'react';
import PropTypes from 'prop-types';
import CustomDataReadOnlyField from '../CustomDataReadOnlyField/CustomDataReadOnlyField';
import List from '../../styled components/List';
import {
  YPAttachmentCollapsibleListItem,
  YPAttachmentListItem
} from './YPAttachment.style';
import YPAttachmentObject from '../../yesplan_classes/YPAttachmentObject';
import YPGeneratedReportObject from '../../yesplan_classes/YPGeneratedReportObject';

/** This component can be used to render a Yesplan Attachment object, including
 *
 *- generated report
 *- custom data field of type attachment;
 *    - document type
 *    - link type
 *
 * Use the auxiliary functions `formatAttachment` and `formatGeneratedReport` in `/libs/ypAPIDataFormatter` to transform data coming from the Yesplan API
 * to the format that is required for this component.
 *
 * When a list of olderAttachments is passed, an expandable list of these attachments is rendered beneath the original attachment.
 *
 * Optionally, a label and a emptyValue can be passed as props.
 */
class YPAttachment extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false
    };
    this.toggleExpanded = this.toggleExpanded.bind(this);
  }

  toggleExpanded() {
    const { expanded } = this.state;
    this.setState({
      expanded: !expanded
    });
  }

  renderAttachment(attachment, withLabel) {
    const { label, emptyValue } = this.props;
    const attachmentValue = attachment || null;
    return (
      <CustomDataReadOnlyField
        type="attachment"
        value={attachmentValue}
        description={attachmentValue && attachmentValue.printDescription()}
        label={withLabel ? label : null}
        emptyValue={emptyValue}
      />
    );
  }

  renderExpandableListOfAttachments() {
    const { expanded } = this.state;
    const { attachment, olderAttachments } = this.props;
    return (
      <YPAttachmentCollapsibleListItem
        label={expanded ? i18n('Hide Older Versions') : i18n('Show Older Versions')}
        labelOptions={{
          size: 'small',
          color: 'gray'
        }}
        iconOptions={{
          size: 'small'
        }}
        onClick={this.toggleExpanded}
        dataAttributes={{ 'data-test-button': 'expand-attachments' }}
      >
        <List
          key={`showall${attachment.id}`}
          data-test-list="olderattachmentsfor"
          $withLeftMargin
          $withPadding={false}
        >
          {olderAttachments.map((report) => (
            <YPAttachmentListItem key={report.id}>
              {this.renderAttachment(report)}
            </YPAttachmentListItem>
          ))}
        </List>
      </YPAttachmentCollapsibleListItem>
    );
  }

  render() {
    /*
   # CSS DOCUMENTATION

   * 1. 'min-w-0': Flex hack to have the class 'break-words' to work in a flex environment.
   *    'break-words': Let the textual content break in a nice way
   */
    const { attachment, olderAttachments, renderMetaInfo } = this.props;
    return (
      <div data-test-type="attachment" tw="min-w-0">
        {/* [1] */}
        {this.renderAttachment(attachment, true)}
        {renderMetaInfo && renderMetaInfo(attachment, olderAttachments)}
        {olderAttachments.length >= 1 && this.renderExpandableListOfAttachments()}
      </div>
    );
  }
}

YPAttachment.propTypes = {
  attachment: PropTypes.oneOfType([
    PropTypes.instanceOf(YPAttachmentObject),
    PropTypes.instanceOf(YPGeneratedReportObject)
  ]),
  olderAttachments: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.instanceOf(YPAttachmentObject),
      PropTypes.instanceOf(YPGeneratedReportObject)
    ])
  ),
  label: PropTypes.string,
  emptyValue: PropTypes.string,
  renderMetaInfo: PropTypes.func
};

YPAttachment.defaultProps = {
  attachment: null,
  olderAttachments: [],
  label: null,
  emptyValue: '',
  renderMetaInfo: null
};

export default YPAttachment;
